// src/components/profile/ProfileSettings.jsx - Comprehensive Profile Settings
import React, { useState, useEffect } from 'react';
import { 
  X, Save, Upload, User, Building, Briefcase, Package, Eye, Heart, Link, FileText, Play,
  MessageCircle, Share2, Grid, List, Palette, Globe, Lock, Users, Printer, DollarSign,
  Type, Settings, Trash2, Loader2, AlertCircle, Check, Monitor, Plus, Move, Info,
  Smartphone, Tablet, Mail, Phone, MapPin, Link as LinkIcon, File, Download,
  Shield, Database, Bell, BarChart3, Code, Layout, UserCheck, Trophy, Image as ImageIcon,
  ExternalLink, Copy, RefreshCw, Calendar, Award, Briefcase as ProjectIcon
} from 'lucide-react';
import { FileUpload } from '../common/FileUpload';
import { profileService } from '../../services/profileService';
import { HeaderSettings } from '../settings/shared/HeaderSettings';
import { DesignBrandingSection } from '../settings/shared/DesignSection';
import { ViewSettings } from '../settings/shared/ViewSettings';

const PROFILE_SECTIONS = [
  {
    key: 'hero',
    name: 'Hero Section',
    description: 'Main introduction and headline',
    icon: User,
    category: 'essential'
  },
  {
    key: 'about',
    name: 'About Me',
    description: 'Personal or professional summary',
    icon: User,
    category: 'essential'
  },
  {
    key: 'experience',
    name: 'Work Experience',
    description: 'Professional work history',
    icon: Briefcase,
    category: 'professional'
  },
  {
    key: 'education',
    name: 'Education',
    description: 'Academic background and qualifications',
    icon: Award,
    category: 'professional'
  },
  {
    key: 'skills',
    name: 'Skills & Expertise',
    description: 'Technical and soft skills',
    icon: Settings,
    category: 'professional'
  },
  {
    key: 'projects',
    name: 'Projects & Portfolio',
    description: 'Showcase of work and projects',
    icon: ProjectIcon,
    category: 'showcase'
  },
  {
    key: 'achievements',
    name: 'Achievements & Awards',
    description: 'Recognition and accomplishments',
    icon: Trophy,
    category: 'showcase'
  },
  {
    key: 'services',
    name: 'Services Offered',
    description: 'Services or offerings available',
    icon: Package,
    category: 'business'
  },
  {
    key: 'references', // NEW SECTION
    name: 'References',
    description: 'Professional or character references',
    icon: Users,
    category: 'professional'
  },
  {
    key: 'testimonials',
    name: 'Testimonials',
    description: 'Client or colleague feedback',
    icon: MessageCircle,
    category: 'social'
  },
  {
    key: 'gallery',
    name: 'Photo Gallery',
    description: 'Image gallery or media showcase',
    icon: ImageIcon,
    category: 'showcase'
  },
  {
    key: 'contact',
    name: 'Contact Information',
    description: 'Ways to get in touch',
    icon: Mail,
    category: 'essential'
  },
  {
    key: 'stats',
    name: 'Statistics',
    description: 'Key metrics and numbers',
    icon: BarChart3,
    category: 'showcase'
  },
  {
    key: 'story',
    name: 'Product/Service Story',
    description: 'Compelling narrative about your offering',
    icon: FileText,
    category: 'essential'
  },
  {
    key: 'demo',
    name: 'Demo & Videos',
    description: 'Product demonstrations and videos',
    icon: Play,
    category: 'showcase'
  },
  {
    key: 'specifications',
    name: 'Specifications',
    description: 'Technical details and requirements',
    icon: FileText,
    category: 'details'
  },
  {
    key: 'credentials',
    name: 'Credentials',
    description: 'Professional certifications and credentials',
    icon: Award,
    category: 'trust'
  },
  {
    key: 'pricing',
    name: 'Pricing',
    description: 'Pricing tiers and packages',
    icon: DollarSign,
    category: 'business'
  },
  {
    key: 'engagement',
    name: 'Customer Engagement',
    description: 'Interest tracking and feedback',
    icon: Heart,
    category: 'social'
  }
];

export const ProfileSettingsModal = ({ profile, book, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    // Basic Info
    botName: '',
    description: '',
    subCategory: 'personal',
    profileImageUrl: '',
    coverImageUrl: '',
    logoUrl: '',
    
    // Profile Display Settings
    layout: 'modern', // modern, classic, minimal, creative
    theme: 'professional', // professional, creative, corporate, personal
    sectionLayout: 'card', // card, list, timeline, grid
    showSectionIcons: true,
    showSectionNumbers: false,
    enableSectionCollapse: false,
    heroStyle: 'banner', // banner, centered, minimal, split
    
    // Section Visibility & Order
    sectionVisibility: {
      hero: true,
      about: true,
      experience: true,
      education: true,
      skills: true,
      projects: true,
      achievements: true,
      services: true,
      references: false,
      testimonials: false,
      gallery: false,
      contact: true,
      stats: false,
      story: false,
      demo: false,
      specifications: false,
      credentials: false,
      pricing: false,
      engagement: false
    },
    sectionOrder: [
      'hero', 
      'about', 
      'story', 
      'experience', 
      'education', 
      'skills', 
      'projects', 
      'achievements', 
      'services', 
      'demo', 
      'specifications', 
      'credentials', 
      'references',
      'testimonials',
      'gallery', 
      'pricing', 
      'engagement', 
      'contact',
      'stats'
    ],
    
    // Styling & Colors
    primaryColor: '#6366f1',
    backgroundColor: '#f8fafc',
    headerColor: '#ffffff',
    headerTextColor: '#1f2937',
    textColor: '#1f2937',
    headerOpacity: 30,
    accentColor: '#8b5cf6',
    fontFamily: 'Helvetica, sans-serif',
    fontSize: 'medium',
    borderRadius: 'medium',
    shadowLevel: 'subtle',
    spacing: 'comfortable',

    headerSettings: {
          showLogo: true,
          showTitle: true,
          showDescription: false,
          showSearch: true,
          showShareButton: true,
          showAccessLevel: true,
          showFilterControls: true,
          showGallerySelector: true,
          showViewModeToggle: true,
          showOwnerControls: true,
          headerBackgroundColor: '#ffffff',
          headerTextColor: '#000000',
          headerHeight: 'normal',
          logoHeight: 40,
          logoPosition: 'left',
          stickyHeader: true,
          showBreadcrumbs: true,
          headerStyle: 'default',
          headerShadow: true,
          userProfileVisibility: 'auto', // auto|always|authenticated|never
          showProfileForPublic: false,
          requireLoginToView: false,
          
        },
    
    // Interaction Settings
    allowLikes: true,
    allowComments: false,
    allowSharing: true,
    showViewCounts: true,
    showProfileViews: true,
    showSectionViews: false,
    enableContactTracking: true,
    allowAnonymousViewing: true,
    
    // Contact & Social Settings
    contactVisibility: {
      email: true,
      phone: true,
      website: true,
      location: true,
      social: true
    },
    contactMethods: {
      preferredContact: 'email', // email, phone, form
      showContactForm: true,
      autoReply: false,
      autoReplyMessage: ''
    },
    socialLinks: {
      linkedin: '',
      twitter: '',
      github: '',
      instagram: '',
      facebook: '',
      youtube: '',
      website: ''
    },
    
    // Privacy & Access
    published: profile.published !== false, 
    accessLevel: 'public', // public, unlisted, private, restricted
    requireLogin: false,
    allowIndexing: true,
    authorizedUsers: [],
    allowPrint: true,
    allowDownload: false, 
    enableRightClickProtection: false,
    showLastUpdate: true,
    
    // Professional Settings (for business profiles)
    businessHours: {
      enabled: false,
      timezone: 'America/New_York',
      schedule: {
        monday: { open: '09:00', close: '17:00', closed: false },
        tuesday: { open: '09:00', close: '17:00', closed: false },
        wednesday: { open: '09:00', close: '17:00', closed: false },
        thursday: { open: '09:00', close: '17:00', closed: false },
        friday: { open: '09:00', close: '17:00', closed: false },
        saturday: { open: '10:00', close: '15:00', closed: true },
        sunday: { open: '10:00', close: '15:00', closed: true }
      }
    },
    
    // SEO & Sharing
    customDomain: '',
    customPath: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    ogImage: '',
    favicon: '',
    enableStructuredData: true,
    allowIndexing: true,
    
    // Advanced Settings
    enableAnalytics: true,
    enableHeatmaps: false,
    customCSS: '',
    customJS: '',
    enableRightClickProtection: false,
    enableDevTools: false,
    enablePrintProtection: false,
    
    // Integration Settings
    integrations: {
      googleAnalytics: { enabled: false, trackingId: '' },
      facebookPixel: { enabled: false, pixelId: '' },
      linkedinInsight: { enabled: false, partnerId: '' },
      calendly: { enabled: false, username: '' },
      stripe: { enabled: false, accountId: '' }
    },
    
    // Notification Settings
    emailNotifications: {
      profileViews: false,
      contactForm: true,
      comments: true,
      likes: false,
      weeklyReport: true
    }
  });

  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Image preview states
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [coverImagePreview, setCoverImagePreview] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [faviconPreview, setFaviconPreview] = useState('');
  const [ogImagePreview, setOgImagePreview] = useState('');
  const [uploadStates, setUploadStates] = useState({});

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Validation states
  const [errors, setErrors] = useState({});
  const [newAuthorizedUser, setNewAuthorizedUser] = useState('');

  // Initialize form data
  useEffect(() => {
    if (profile) {
      setFormData(prev => ({
        ...prev,
        // Basic Info
        botName: profile.botName || '',
        description: profile.description || '',
        subCategory: profile.subCategory || 'personal',
        profileImageUrl: profile.profileImageUrl || '',
        coverImageUrl: profile.coverImageUrl || '',
        logoUrl: profile.logoUrl || '',

        headerSettings: {
          showLogo: profile?.headerSettings?.showLogo ?? book?.headerSettings?.showLogo ?? true,
          showTitle: profile?.headerSettings?.showTitle ?? book?.headerSettings?.showTitle ?? true,
          showDescription: profile?.headerSettings?.showDescription ?? book?.headerSettings?.showDescription ?? false,
          showSearch: profile?.headerSettings?.showSearch ?? book?.headerSettings?.showSearch ?? true,
          showShareButton: profile?.headerSettings?.showShareButton ?? book?.headerSettings?.showShareButton ?? true,
          showAccessLevel: profile?.headerSettings?.showAccessLevel ?? book?.headerSettings?.showAccessLevel ?? true,
          showFilterControls: profile?.headerSettings?.showFilterControls ?? book?.headerSettings?.showFilterControls ?? true,
          showGallerySelector: profile?.headerSettings?.showGallerySelector ?? book?.headerSettings?.showGallerySelector ?? true,
          showViewModeToggle: profile?.headerSettings?.showViewModeToggle ?? book?.headerSettings?.showViewModeToggle ?? true,
          showOwnerControls: profile?.headerSettings?.showOwnerControls ?? book?.headerSettings?.showOwnerControls ?? true,
          headerBackgroundColor: profile?.headerSettings?.headerBackgroundColor || book?.headerSettings?.headerBackgroundColor || '#ffffff',
          headerTextColor: profile?.headerSettings?.headerTextColor || book?.headerSettings?.headerTextColor || '#000000',
          headerHeight: profile?.headerSettings?.headerHeight || book?.headerSettings?.headerHeight || 'normal',
          logoHeight: profile?.headerSettings?.logoHeight || book?.headerSettings?.logoHeight || 40,
          logoPosition: profile?.headerSettings?.logoPosition || book?.headerSettings?.logoPosition || 'left',
          stickyHeader: profile?.headerSettings?.stickyHeader ?? book?.headerSettings?.stickyHeader ?? true,
          showBreadcrumbs: profile?.headerSettings?.showBreadcrumbs ?? book?.headerSettings?.showBreadcrumbs ?? true,
          headerStyle: profile?.headerSettings?.headerStyle || book?.headerSettings?.headerStyle || 'default',
          headerShadow: profile?.headerSettings?.headerShadow ?? book?.headerSettings?.headerShadow ?? true,

          userProfileVisibility: profile?.headerSettings?.userProfileVisibility || 'auto',
          showProfileForPublic: profile?.headerSettings?.showProfileForPublic ?? false,
          requireLoginToView: profile?.headerSettings?.requireLoginToView ?? false,
        },
        
        // SEO Settings - NEW
        customDomain: profile.customDomain || '',
        customPath: profile.customPath || '',
        seoTitle: profile.seoTitle || profile.botName || '',
        seoDescription: profile.seoDescription || profile.description || '',
        seoKeywords: profile.seoKeywords || '',
        ogImage: profile.ogImage || profile.coverImageUrl || '',
        favicon: profile.favicon || '',
        enableStructuredData: profile.enableStructuredData !== false,
        allowIndexing: profile.allowIndexing !== false,
        
        // Profile Display Settings
        layout: profile.profileSettings?.layout || prev.layout,
        theme: profile.profileSettings?.theme || prev.theme,
        sectionLayout: profile.profileSettings?.sectionLayout || prev.sectionLayout,
        showSectionIcons: profile.profileSettings?.showSectionIcons !== false,
        showSectionNumbers: profile.profileSettings?.showSectionNumbers || false,
        enableSectionCollapse: profile.profileSettings?.enableSectionCollapse || false,
        heroStyle: profile.profileSettings?.heroStyle || prev.heroStyle,
        
        // Section Visibility & Order
        sectionVisibility: {
          ...prev.sectionVisibility,
          ...profile.profileSettings?.sectionVisibility,
          references: profile.profileSettings?.sectionVisibility?.references || false, 
          story: profile.profileSettings?.sectionVisibility?.story || false,
          demo: profile.profileSettings?.sectionVisibility?.demo || false,
          specifications: profile.profileSettings?.sectionVisibility?.specifications || false,
          credentials: profile.profileSettings?.sectionVisibility?.credentials || false,
          pricing: profile.profileSettings?.sectionVisibility?.pricing || false,
          engagement: profile.profileSettings?.sectionVisibility?.engagement || false
        },

        sectionOrder: profile.profileSettings?.sectionOrder || [
          'hero', 'about', 'story', 'experience', 'education', 'skills', 
          'projects', 'achievements', 'services', 'demo', 'specifications',
          'credentials', 'references', 'testimonials', 'gallery', 'pricing',
          'engagement', 'contact', 'stats'
        ],

        viewSettings: profile?.viewSettings || {
          enablePublicView: true,
          enableManagementView: true,
          publicViewLabel: 'Store',
          managementViewLabel: 'Admin Panel',
          defaultView: 'public',
          requireAuth: false,
          allowedRoles: ['owner', 'admin', 'manager']
        },
        permissions: profile.permissions || {},
        authorizedEmails: profile.authorizedEmails || [],
          
        // Styling & Colors
        primaryColor: profile.styling?.primaryColor || prev.primaryColor,
        backgroundColor: profile.styling?.backgroundColor || prev.backgroundColor,
        headerColor: profile.styling?.headerColor || prev.headerColor,
        headerTextColor: profile.styling?.headerTextColor || prev.headerTextColor,
        textColor: profile.styling?.textColor || prev.textColor,
        headerOpacity: profile.styling?.headerOpacity ?? prev.headerOpacity,
        accentColor: profile.styling?.accentColor || prev.accentColor,
        fontFamily: profile.styling?.fontFamily || prev.fontFamily,
        fontSize: profile.styling?.fontSize || prev.fontSize,
        borderRadius: profile.styling?.borderRadius || prev.borderRadius,
        shadowLevel: profile.styling?.shadowLevel || prev.shadowLevel,
        spacing: profile.styling?.spacing || prev.spacing,
        
        // Privacy & Access
        published: profile.published !== false,
        accessLevel: profile.accessLevel || 'public',
        requireLogin: profile.requireLogin || false,
        allowIndexing: profile.allowIndexing !== false,
        authorizedUsers: profile.authorizedUsers || [],
        allowPrint: profile.allowPrint !== false,
        allowDownload: profile.allowDownload || false, // NEW
        enableRightClickProtection: profile.enableRightClickProtection || false, // NEW
        showLastUpdate: profile.showLastUpdate !== false,
        
        // Interaction Settings
        allowLikes: profile.allowLikes !== false,
        allowComments: profile.allowComments || false,
        allowSharing: profile.allowSharing !== false,
        showViewCounts: profile.showViewCounts !== false,
        showProfileViews: profile.showProfileViews !== false,
        showSectionViews: profile.showSectionViews || false,
        enableContactTracking: profile.enableContactTracking !== false,
        allowAnonymousViewing: profile.allowAnonymousViewing !== false,
        
        // Contact & Social Settings
        contactVisibility: profile.contactVisibility || prev.contactVisibility,
        contactMethods: profile.contactMethods || prev.contactMethods,
        socialLinks: profile.socialLinks || prev.socialLinks,
        
        // Professional Settings
        businessHours: profile.businessHours || prev.businessHours,
        
        // Advanced Settings
        enableAnalytics: profile.enableAnalytics !== false,
        enableHeatmaps: profile.enableHeatmaps || false,
        customCSS: profile.customCSS || '',
        customJS: profile.customJS || '',
        
        // Integration Settings
        integrations: profile.integrations || prev.integrations,
        
        // Notification Settings
        emailNotifications: profile.emailNotifications || prev.emailNotifications
      }));

      // Set preview images
      setProfileImagePreview(profile.profileImageUrl || '');
      setCoverImagePreview(profile.coverImageUrl || '');
      setLogoPreview(profile.logoUrl || '');
      setFaviconPreview(profile.favicon || '');
      setOgImagePreview(profile.ogImage || '');
    }
  }, [profile]);

  const handleNestedInputChange = (parent, key, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [key]: value
      }
    }));
  };

  const handleProfileTypeChange = (newProfileType) => {
    // Define section visibility defaults for each profile type
    const profileTypeDefaults = {
      personal: {
        // Traditional personal profile sections
        hero: true,
        about: true,
        experience: true,
        education: true,
        skills: true,
        projects: true,
        achievements: true,
        references: false,
        testimonials: false,
        gallery: false,
        contact: true,
        stats: false,
        // Product/service sections off
        story: false,
        demo: false,
        specifications: false,
        credentials: false,
        pricing: false,
        engagement: false,
        services: false
      },
      organization: {
        // Organization profile sections
        hero: true,
        about: true,
        services: true,
        testimonials: true,
        gallery: true,
        references: true,
        stats: true,
        contact: true,
        // Traditional sections off
        experience: false,
        education: false,
        skills: false,
        projects: false,
        achievements: false,
        // Product/service sections available but off by default
        story: false,
        demo: false,
        specifications: false,
        credentials: false,
        pricing: false,
        engagement: false
      },
      project: {
        // Project profile sections
        hero: true,
        about: true,
        features: true,
        gallery: true,
        references: true,
        contact: true,
        // Traditional sections off
        experience: false,
        education: false,
        services: false,
        testimonials: false,
        stats: false,
        // Product/service sections off
        story: false,
        demo: false,
        specifications: false,
        credentials: false,
        pricing: false,
        engagement: false
      },
      asset: {
        // Asset profile sections
        hero: true,
        about: true,
        specifications: true,
        pricing: true,
        gallery: true,
        contact: true,
        // Traditional sections off
        experience: false,
        education: false,
        skills: false,
        projects: false,
        services: false,
        // Some product/service sections available
        story: false,
        demo: false,
        credentials: false,
        engagement: false,
        testimonials: false,
        references: false,
        stats: false
      },
      product: {
        // Product profile sections - NEW
        hero: true,
        about: true,
        story: true,           // ON for products
        features: true,
        demo: true,            // ON for products
        specifications: true,  // ON for products
        gallery: true,
        credentials: true,     // ON for products
        testimonials: true,
        pricing: true,         // ON for products
        engagement: true,      // ON for products
        contact: true,
        // Traditional sections off
        experience: false,
        education: false,
        skills: false,
        projects: false,
        achievements: false,
        services: false,
        references: false,
        stats: false
      },
      service: {
        // Service profile sections - NEW
        hero: true,
        about: true,
        story: true,           // ON for services
        services: true,
        demo: true,            // ON for services
        gallery: true,
        credentials: true,     // ON for services
        testimonials: true,
        pricing: true,         // ON for services
        engagement: true,      // ON for services
        contact: true,
        // Traditional sections off
        experience: false,
        education: false,
        skills: false,
        projects: false,
        achievements: false,
        features: false,
        specifications: false,
        references: false,
        stats: false
      }
    };

    const defaults = profileTypeDefaults[newProfileType] || profileTypeDefaults.personal;
    
    setFormData(prev => ({
      ...prev,
      subCategory: newProfileType,
      sectionVisibility: {
        ...prev.sectionVisibility,
        ...defaults
      }
    }));
  };

  // Update handleInputChange to NOT handle subCategory (since we have dedicated handler):
  const handleInputChange = (path, value) => {
    console.log('Input change:', path, value); // Debug log
    
    setFormData(prev => {
      // Handle nested paths with dot notation
      if (path.includes('.')) {
        const keys = path.split('.');
        const newData = { ...prev };
        
        // Navigate to parent object
        let current = newData;
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          
          // Ensure nested object exists
          if (!current[key] || typeof current[key] !== 'object') {
            current[key] = {};
          } else {
            current[key] = { ...current[key] };
          }
          
          current = current[key];
        }
        
        // Set the final value
        current[keys[keys.length - 1]] = value;
        
        return newData;
      }
      
      // Handle simple top-level properties
      return {
        ...prev,
        [path]: value
      };
    });
  };

  const getProfileTypeDefaults = (profileType) => {
    const defaults = {
      personal: {
        sectionVisibility: {
          hero: true,
          about: true,
          experience: true,
          education: true,
          skills: true,
          projects: true,
          achievements: true,
          references: false,
          testimonials: false,
          gallery: false,
          contact: true,
          // Product/service sections off by default for personal
          story: false,
          demo: false,
          specifications: false,
          credentials: false,
          pricing: false,
          engagement: false
        }
      },
      product: {
        sectionVisibility: {
          hero: true,
          about: true,
          story: true, // ON for product profiles
          features: true,
          demo: true, // ON for product profiles  
          specifications: true, // ON for product profiles
          gallery: true,
          credentials: true, // ON for product profiles
          testimonials: true,
          pricing: true, // ON for product profiles
          engagement: true, // ON for product profiles
          contact: true,
          // Traditional sections off by default for products
          experience: false,
          education: false,
          references: false
        }
      },
      service: {
        sectionVisibility: {
          hero: true,
          about: true,
          story: true, // ON for service profiles
          services: true,
          demo: true, // ON for service profiles
          gallery: true,
          credentials: true, // ON for service profiles
          testimonials: true,
          pricing: true, // ON for service profiles
          engagement: true, // ON for service profiles
          contact: true,
          // Traditional sections off by default for services
          experience: false,
          education: false,
          projects: false,
          references: false
        }
      },
      organization: {
        sectionVisibility: {
          hero: true,
          about: true,
          services: true,
          team: true,
          testimonials: true,
          gallery: true,
          references: true,
          stats: true,
          contact: true,
          // Product/service sections available but off by default
          story: false,
          demo: false,
          credentials: false,
          pricing: false,
          engagement: false
        }
      }
    };
    
    return defaults[profileType] || defaults.personal;
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors({});
    
    try {
      // Validate required fields
      const validationErrors = {};
      
      if (!formData.botName.trim()) {
        validationErrors.botName = 'Profile name is required';
      }
      
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setSaving(false);
        return;
      }

      // Create the updated profile object
      const updatedProfile = {
        // Basic Info
        botName: formData.botName.trim(),
        description: formData.description.trim(),
        subCategory: formData.subCategory,
        profileImageUrl: formData.profileImageUrl,
        coverImageUrl: formData.coverImageUrl,
        logoUrl: formData.logoUrl,
        
        // SEO Settings
        customDomain: formData.customDomain,
        customPath: formData.customPath,
        seoTitle: formData.seoTitle,
        seoDescription: formData.seoDescription,
        seoKeywords: formData.seoKeywords,
        ogImage: formData.ogImage,
        favicon: formData.favicon,
        enableStructuredData: formData.enableStructuredData,
        allowIndexing: formData.allowIndexing,
        
        // Privacy Settings
        published: formData.published,
        accessLevel: formData.accessLevel,
        authorizedUsers: formData.authorizedUsers,
        requireLogin: formData.requireLogin,

        headerSettings: {
          showLogo: formData.headerSettings?.showLogo ?? true,
          showTitle: formData.headerSettings?.showTitle ?? true,
          showDescription: formData.headerSettings?.showDescription ?? false,
          showSearch: formData.headerSettings?.showSearch ?? true,
          showShareButton: formData.headerSettings?.showShareButton ?? true,
          showAccessLevel: formData.headerSettings?.showAccessLevel ?? true,
          showFilterControls: formData.headerSettings?.showFilterControls ?? true,
          showGallerySelector: formData.headerSettings?.showGallerySelector ?? true,
          showViewModeToggle: formData.headerSettings?.showViewModeToggle ?? true,
          showOwnerControls: formData.headerSettings?.showOwnerControls ?? true,
          headerBackgroundColor: formData.headerSettings?.headerBackgroundColor || '#ffffff',
          headerTextColor: formData.headerSettings?.headerTextColor || '#000000',
          headerHeight: formData.headerSettings?.headerHeight || 'normal',
          logoHeight: formData.headerSettings?.logoHeight || 40,
          logoPosition: formData.headerSettings?.logoPosition || 'left',
          stickyHeader: formData.headerSettings?.stickyHeader ?? true,
          showBreadcrumbs: formData.headerSettings?.showBreadcrumbs ?? true,
          headerStyle: formData.headerSettings?.headerStyle || 'default',
          headerShadow: formData.headerSettings?.headerShadow ?? true,

          userProfileVisibility: formData.headerSettings?.userProfileVisibility ?? 'auto',
          showProfileForPublic: formData.headerSettings?.showProfileForPublic ?? false,
          requireLoginToView: formData.headerSettings?.requireLoginToView ?? false,
        },

        viewSettings: formData.viewSettings,
        permissions: formData.permissions,
        authorizedEmails: formData.authorizedEmails,
        
        // Profile Settings object - UPDATED to include new sections
        profileSettings: {
          layout: formData.layout,
          theme: formData.theme,
          sectionLayout: formData.sectionLayout,
          showSectionIcons: formData.showSectionIcons,
          showSectionNumbers: formData.showSectionNumbers,
          enableSectionCollapse: formData.enableSectionCollapse,
          heroStyle: formData.heroStyle,
          
          // Section visibility and order with new sections
          sectionVisibility: formData.sectionVisibility,
          sectionOrder: formData.sectionOrder,
          
          // Interaction settings
          allowLikes: formData.allowLikes,
          allowComments: formData.allowComments,
          allowSharing: formData.allowSharing,
          showViewCounts: formData.showViewCounts,
          showProfileViews: formData.showProfileViews,
          showSectionViews: formData.showSectionViews,
          enableContactTracking: formData.enableContactTracking,
          allowAnonymousViewing: formData.allowAnonymousViewing,
          
          // Contact & Social Settings
          contactVisibility: formData.contactVisibility,
          contactMethods: formData.contactMethods,
          socialLinks: formData.socialLinks,
          
          // Professional Settings
          businessHours: formData.businessHours,
          
          // Advanced Settings
          enableAnalytics: formData.enableAnalytics,
          enableHeatmaps: formData.enableHeatmaps,
          customCSS: formData.customCSS,
          customJS: formData.customJS,
          
          // Integration Settings
          integrations: formData.integrations,
          
          // Notification Settings
          emailNotifications: formData.emailNotifications,
          
          // Other settings
          showLastUpdate: formData.showLastUpdate
        },
        
        // Styling object
        styling: {
          primaryColor: formData.primaryColor,
          backgroundColor: formData.backgroundColor,
          headerColor: formData.headerColor,
          headerTextColor: formData.headerTextColor,
          headerOpacity: formData.headerOpacity,
          textColor: formData.textColor,
          accentColor: formData.accentColor,
          fontFamily: formData.fontFamily,
          fontSize: formData.fontSize,
          borderRadius: formData.borderRadius,
          shadowLevel: formData.shadowLevel,
          spacing: formData.spacing
        },
        
        // Update timestamps
        updatedAt: new Date().toISOString(),
        lastModified: new Date().toISOString()
      };

      // Save the profile using the service
      await onSave(updatedProfile);
      
      // Show success message
      console.log('Profile settings saved successfully');
      
    } catch (error) {
      console.error('Error saving profile settings:', error);
      setErrors({ save: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Add form validation function
  const validateForm = () => {
    const errors = {};
    
    // Basic validation
    if (!formData.botName?.trim()) {
      errors.botName = 'Profile name is required';
    }
    
    // Custom domain validation
    if (formData.customDomain && !isValidDomain(formData.customDomain)) {
      errors.customDomain = 'Please enter a valid domain (e.g., profile.yourdomain.com)';
    }
    
    // Custom path validation
    if (formData.customPath && !/^[a-zA-Z0-9-]+$/.test(formData.customPath)) {
      errors.customPath = 'Path can only contain letters, numbers, and hyphens';
    }
    
    // SEO validation
    if (formData.seoTitle && formData.seoTitle.length > 60) {
      errors.seoTitle = 'SEO title should be 60 characters or less';
    }
    
    if (formData.seoDescription && formData.seoDescription.length > 160) {
      errors.seoDescription = 'SEO description should be 160 characters or less';
    }
    
    return errors;
  };

  // Helper function for domain validation
  const isValidDomain = (domain) => {
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
    return domainRegex.test(domain);
  };

  const handleFileUpload = async (file, uploadType, fieldName) => {
    if (!file) return;

    const uploadKey = `${uploadType}_${fieldName}`;
    setUploadStates(prev => ({ ...prev, [uploadKey]: true }));
    setErrors(prev => ({ ...prev, [fieldName]: '' }));

    try {
      const result = await profileService.uploadProfileMedia(
        file, 
        profile?.id || 'temp', 
        uploadType,
        fieldName
      );
      
      // Update form data with new URL
      handleInputChange(fieldName, result.url);
      
      // Update preview
      switch (fieldName) {
        case 'profileImageUrl':
          setProfileImagePreview(result.url);
          break;
        case 'coverImageUrl':
          setCoverImagePreview(result.url);
          break;
        case 'logoUrl':
          setLogoPreview(result.url);
          break;
        case 'favicon':
          setFaviconPreview(result.url);
          break;
        case 'ogImage':
          setOgImagePreview(result.url);
          break;
      }
      
      console.log(`${fieldName} uploaded successfully:`, result.url);
      
    } catch (error) {
      console.error(`Error uploading ${fieldName}:`, error);
      setErrors(prev => ({ ...prev, [fieldName]: error.message }));
    } finally {
      setUploadStates(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  // Simplified file upload handlers for each type
  const handleProfileImageUpload = (file, error) => {
    if (error) {
      setErrors(prev => ({ ...prev, profileImage: error }));
      return;
    }
    handleFileUpload(file, 'profile', 'profileImageUrl');
  };

  const handleCoverImageUpload = (file, error) => {
    if (error) {
      setErrors(prev => ({ ...prev, coverImage: error }));
      return;
    }
    handleFileUpload(file, 'profile', 'coverImageUrl');
  };

  const handleLogoUpload = (file, error) => {
    if (error) {
      setErrors(prev => ({ ...prev, logo: error }));
      return;
    }
    handleFileUpload(file, 'profile', 'logoUrl');
  };

  const handleFaviconUpload = (file, error) => {
    if (error) {
      setErrors(prev => ({ ...prev, favicon: error }));
      return;
    }
    handleFileUpload(file, 'seo', 'favicon');
  };

  const handleOgImageUpload = (file, error) => {
    if (error) {
      setErrors(prev => ({ ...prev, ogImage: error }));
      return;
    }
    handleFileUpload(file, 'seo', 'ogImage');
  };

  const getVisibleSections = () => {
    return formData.sectionOrder.filter(sectionKey => 
      formData.sectionVisibility[sectionKey]
    );
  };

  // Get hidden sections
  const getHiddenSections = () => {
    return PROFILE_SECTIONS
      .map(section => section.key)
      .filter(sectionKey => !formData.sectionVisibility[sectionKey]);
  };

  // Handle section visibility change
  const handleSectionVisibilityChange = (sectionKey, isVisible) => {
    const updatedVisibility = {
      ...formData.sectionVisibility,
      [sectionKey]: isVisible
    };
    
    let updatedOrder = [...formData.sectionOrder];
    
    if (isVisible) {
      // Add to order if not already present
      if (!updatedOrder.includes(sectionKey)) {
        // Find the default position for this section based on PROFILE_SECTIONS order
        const defaultIndex = PROFILE_SECTIONS.findIndex(s => s.key === sectionKey);
        const insertIndex = updatedOrder.findIndex(key => {
          const keyIndex = PROFILE_SECTIONS.findIndex(s => s.key === key);
          return keyIndex > defaultIndex;
        });
        
        if (insertIndex === -1) {
          updatedOrder.push(sectionKey);
        } else {
          updatedOrder.splice(insertIndex, 0, sectionKey);
        }
      }
    } else {
      // Remove from order
      updatedOrder = updatedOrder.filter(key => key !== sectionKey);
    }
    
    setFormData(prev => ({
      ...prev,
      sectionVisibility: updatedVisibility,
      sectionOrder: updatedOrder
    }));
  };

  // Reset section order to default
  const resetSectionOrder = () => {
    const defaultOrder = PROFILE_SECTIONS.map(section => section.key);
    setFormData(prev => ({
      ...prev,
      sectionOrder: defaultOrder
    }));
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      return;
    }
    
    const visibleSections = getVisibleSections();
    const newOrder = [...visibleSections];
    
    // Remove dragged item
    const draggedItem = newOrder.splice(draggedIndex, 1)[0];
    
    // Insert at new position
    newOrder.splice(dropIndex, 0, draggedItem);
    
    // Update the full section order array
    const updatedOrder = [...formData.sectionOrder];
    
    // Remove all visible sections from the order
    visibleSections.forEach(section => {
      const index = updatedOrder.indexOf(section);
      if (index > -1) {
        updatedOrder.splice(index, 1);
      }
    });
    
    // Insert the reordered visible sections at the beginning
    updatedOrder.unshift(...newOrder);
    
    setFormData(prev => ({
      ...prev,
      sectionOrder: updatedOrder
    }));
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleExportProfile = async (format) => {
    try {
      const exportData = {
        // Basic Information
        basic: {
          botName: formData.botName,
          description: formData.description,
          subCategory: formData.subCategory,
          profileImageUrl: formData.profileImageUrl,
          coverImageUrl: formData.coverImageUrl,
          logoUrl: formData.logoUrl,
          createdAt: profile.createdAt,
          updatedAt: new Date().toISOString()
        },
        
        // SEO Settings
        seo: {
          customDomain: formData.customDomain,
          customPath: formData.customPath,
          seoTitle: formData.seoTitle,
          seoDescription: formData.seoDescription,
          seoKeywords: formData.seoKeywords,
          ogImage: formData.ogImage,
          favicon: formData.favicon,
          enableStructuredData: formData.enableStructuredData
        },
        
        // Privacy Settings
        privacy: {
          accessLevel: formData.accessLevel,
          requireLogin: formData.requireLogin,
          allowIndexing: formData.allowIndexing,
          allowPrint: formData.allowPrint,
          enableRightClickProtection: formData.enableRightClickProtection
        },
        
        // Styling Settings
        styling: {
          primaryColor: formData.primaryColor,
          backgroundColor: formData.backgroundColor,
          headerColor: formData.headerColor,
          fontFamily: formData.fontFamily,
          theme: formData.theme,
          layout: formData.layout
        },
        
        // Analytics (if available)
        analytics: profile.analytics || {
          totalViews: 0,
          uniqueViews: 0,
          interactions: 0
        },
        
        // Export metadata
        exportMetadata: {
          exportedAt: new Date().toISOString(),
          exportFormat: format,
          exportedBy: profile.ownerId || 'current_user',
          version: '1.0'
        }
      };

      // Get profile sections if available
      try {
        const sections = await profileService.getProfileSections(profile.id);
        exportData.sections = sections;
      } catch (error) {
        console.warn('Could not load profile sections for export:', error);
        exportData.sections = [];
      }

      const fileName = `${formData.botName || 'profile'}_export_${new Date().toISOString().split('T')[0]}`;

      if (format === 'json') {
        // JSON Export
        const jsonBlob = new Blob([JSON.stringify(exportData, null, 2)], { 
          type: 'application/json' 
        });
        downloadFile(jsonBlob, `${fileName}.json`);
        
      } else if (format === 'csv') {
        // CSV Export
        const csvContent = convertToCSV(exportData);
        const csvBlob = new Blob([csvContent], { type: 'text/csv' });
        downloadFile(csvBlob, `${fileName}.csv`);
        
      } else if (format === 'excel') {
        // Excel Export (using CSV format for now)
        const csvContent = convertToCSV(exportData);
        const csvBlob = new Blob([csvContent], { type: 'application/vnd.ms-excel' });
        downloadFile(csvBlob, `${fileName}.xls`);
      }

    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  };

  const convertToCSV = (data) => {
    const rows = [];
    
    // Add headers
    rows.push(['Section', 'Field', 'Value']);
    
    // Process each section
    Object.entries(data).forEach(([sectionName, sectionData]) => {
      if (typeof sectionData === 'object' && sectionData !== null) {
        if (Array.isArray(sectionData)) {
          // Handle arrays (like sections)
          sectionData.forEach((item, index) => {
            if (typeof item === 'object') {
              Object.entries(item).forEach(([key, value]) => {
                rows.push([`${sectionName}[${index}]`, key, String(value || '')]);
              });
            } else {
              rows.push([sectionName, index.toString(), String(item || '')]);
            }
          });
        } else {
          // Handle objects
          Object.entries(sectionData).forEach(([key, value]) => {
            rows.push([sectionName, key, String(value || '')]);
          });
        }
      } else {
        rows.push([sectionName, '', String(sectionData || '')]);
      }
    });
    
    // Convert to CSV string
    return rows.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
  };

  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const profileTypes = [
    { id: 'personal', name: 'Personal Profile', icon: User, description: 'Individual resume and portfolio' },
    { id: 'organization', name: 'Organization Profile', icon: Building, description: 'Company or business profile' },
    { id: 'project', name: 'Project Profile', icon: ProjectIcon, description: 'Project showcase and details' },
    { id: 'asset', name: 'Asset Profile', icon: Package, description: 'Product or service showcase' },
    { id: 'product', name: 'Product Profile', icon: Package, description: 'Showcase physical or digital products' },
    { id: 'service', name: 'Service Profile', icon: Settings, description: 'Promote professional services' }
  ];

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Type },
    { id: 'display', label: 'Profile Display', icon: Layout },
    { id: 'header', label: 'Header', icon: Layout },
    { id: 'sections', label: 'Section Management', icon: Grid },
    { id: 'design', label: 'Brand Design', icon: Palette },
    { id: 'view', label: 'View Settings', icon: Globe },
    { id: 'interactions', label: 'Interactions', icon: Heart },
    { id: 'contact', label: 'Contact & Social', icon: Mail },
    { id: 'privacy', label: 'Privacy & Access', icon: Lock },
    { id: 'seo', label: 'SEO & Sharing', icon: Globe },
    { id: 'integrations', label: 'Integrations', icon: Settings },
    { id: 'advanced', label: 'Advanced', icon: Code },
    { id: 'preview', label: 'Preview', icon: Eye }
  ];

  if (!profile) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-purple-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserCheck className="h-6 w-6" />
            <div>
              <h2 className="text-xl font-semibold">Profile Settings</h2>
              <p className="text-purple-200 text-sm capitalize">{formData.subCategory} Profile</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-purple-700 hover:bg-purple-800 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-purple-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex h-[70vh]">
          {/* Sidebar - Tabs */}
          <div className="w-72 border-r border-gray-200 p-4 overflow-y-auto bg-gray-50">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors text-sm ${
                      activeTab === tab.id
                        ? 'bg-purple-100 text-purple-700 border border-purple-200 shadow-sm'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <IconComponent className="h-4 w-4 flex-shrink-0" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {activeTab === 'basic' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                
                {/* Profile Name */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profile Name *
                  </label>
                  <input
                    type="text"
                    value={formData.botName}
                    onChange={(e) => handleInputChange('botName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${
                      errors.botName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter profile name"
                  />
                  {errors.botName && (
                    <p className="text-red-500 text-xs mt-1">{errors.botName}</p>
                  )}
                </div>

                {/* Profile Type Selection - Card Interface */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Profile Type *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {profileTypes.map((profileType) => {
                      const IconComponent = profileType.icon;
                      return (
                        <button
                          key={profileType.id}
                          type="button"
                          onClick={() => handleProfileTypeChange(profileType.id)}
                          className={`p-4 border-2 rounded-xl text-left transition-all hover:shadow-md ${
                            formData.subCategory === profileType.id
                              ? 'border-purple-500 bg-purple-50 shadow-md'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <IconComponent className={`h-6 w-6 mt-1 flex-shrink-0 ${
                              formData.subCategory === profileType.id ? 'text-purple-600' : 'text-gray-400'
                            }`} />
                            <div className="flex-1">
                              <h4 className={`font-medium ${
                                formData.subCategory === profileType.id ? 'text-purple-900' : 'text-gray-900'
                              }`}>
                                {profileType.name}
                              </h4>
                              <p className={`text-sm mt-1 ${
                                formData.subCategory === profileType.id ? 'text-purple-700' : 'text-gray-600'
                              }`}>
                                {profileType.description}
                              </p>
                            </div>
                            {formData.subCategory === profileType.id && (
                              <div className="flex-shrink-0">
                                <Check className="h-5 w-5 text-purple-600" />
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Show guidance for new profile types */}
                {(formData.subCategory === 'product' || formData.subCategory === 'service') && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-blue-900">
                          {formData.subCategory === 'product' ? 'Product Profile Features' : 'Service Profile Features'}
                        </h4>
                        <p className="text-sm text-blue-700 mt-1">
                          {formData.subCategory === 'product' 
                            ? 'Perfect for showcasing products with compelling stories, interactive demos, detailed specifications, transparent pricing, and customer engagement features.'
                            : 'Ideal for promoting services with process explanations, professional credentials, client testimonials, flexible pricing, and lead generation capabilities.'
                          }
                        </p>
                        <div className="mt-3">
                          <p className="text-sm font-medium text-blue-800">Automatically enabled sections:</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {formData.subCategory === 'product' 
                              ? ['Story', 'Demo', 'Specifications', 'Credentials', 'Pricing', 'Engagement'].map(section => (
                                  <span key={section} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                                    <Check className="h-3 w-3 mr-1" />
                                    {section}
                                  </span>
                                ))
                              : ['Story', 'Demo', 'Credentials', 'Pricing', 'Engagement'].map(section => (
                                  <span key={section} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                                    <Check className="h-3 w-3 mr-1" />
                                    {section}
                                  </span>
                                ))
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Description */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Brief description of your profile"
                  />
                </div>

                {/* Profile Images Section */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Profile Images</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <FileUpload
                        label="Profile Image"
                        currentFile={formData.profileImageUrl}
                        onFileSelect={handleProfileImageUpload}
                        onRemove={() => handleInputChange('profileImageUrl', '')}
                        accept="image/*"
                        maxSize={5}
                        uploading={uploadStates.profileImage}
                        showPreview={true}
                      />
                    </div>
                    
                    <div>
                      <FileUpload
                        label="Cover Image"
                        currentFile={formData.coverImageUrl}
                        onFileSelect={handleCoverImageUpload}
                        onRemove={() => handleInputChange('coverImageUrl', '')}
                        accept="image/*"
                        maxSize={10}
                        uploading={uploadStates.coverImage}
                        showPreview={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

            {activeTab === 'display' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Display Options</h3>
                  
                  {/* Layout Selection */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Profile Layout
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { id: 'modern', name: 'Modern', desc: 'Clean and contemporary' },
                          { id: 'classic', name: 'Classic', desc: 'Traditional professional' },
                          { id: 'minimal', name: 'Minimal', desc: 'Simple and focused' },
                          { id: 'creative', name: 'Creative', desc: 'Bold and artistic' }
                        ].map(layout => (
                          <div
                            key={layout.id}
                            onClick={() => handleInputChange('layout', layout.id)}
                            className={`p-3 border-2 rounded-lg cursor-pointer text-center transition-colors ${
                              formData.layout === layout.id
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="font-medium text-sm">{layout.name}</div>
                            <div className="text-xs text-gray-600">{layout.desc}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Hero Section Style
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { id: 'banner', name: 'Banner', desc: 'Full width header' },
                          { id: 'centered', name: 'Centered', desc: 'Centered content' },
                          { id: 'minimal', name: 'Minimal', desc: 'Simple header' },
                          { id: 'split', name: 'Split', desc: 'Two column layout' }
                        ].map(style => (
                          <div
                            key={style.id}
                            onClick={() => handleInputChange('heroStyle', style.id)}
                            className={`p-3 border-2 rounded-lg cursor-pointer text-center transition-colors ${
                              formData.heroStyle === style.id
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="font-medium text-sm">{style.name}</div>
                            <div className="text-xs text-gray-600">{style.desc}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Section Layout
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { id: 'card', name: 'Cards', desc: 'Card-based sections' },
                          { id: 'list', name: 'List', desc: 'List-style layout' },
                          { id: 'timeline', name: 'Timeline', desc: 'Chronological flow' },
                          { id: 'grid', name: 'Grid', desc: 'Grid arrangement' }
                        ].map(layout => (
                          <div
                            key={layout.id}
                            onClick={() => handleInputChange('sectionLayout', layout.id)}
                            className={`p-3 border-2 rounded-lg cursor-pointer text-center transition-colors ${
                              formData.sectionLayout === layout.id
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="font-medium text-sm">{layout.name}</div>
                            <div className="text-xs text-gray-600">{layout.desc}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Display Options */}
                  <div className="space-y-4 mt-6">
                    <h4 className="font-medium text-gray-900">Display Options</h4>
                    {[
                      { key: 'showSectionIcons', label: 'Show Section Icons', desc: 'Display icons next to section titles' },
                      { key: 'showSectionNumbers', label: 'Show Section Numbers', desc: 'Number each section sequentially' },
                      { key: 'enableSectionCollapse', label: 'Enable Section Collapse', desc: 'Allow users to collapse sections' },
                      { key: 'showLastUpdate', label: 'Show Last Updated', desc: 'Display when profile was last updated' }
                    ].map(({ key, label, desc }) => (
                      <label key={key} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <input
                          type="checkbox"
                          checked={formData[key]}
                          onChange={(e) => handleInputChange(key, e.target.checked)}
                          className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">{label}</div>
                          <div className="text-sm text-gray-500">{desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'header' && (
              <HeaderSettings
                formData={formData}
                onInputChange={handleInputChange}              
                onImageUpload={handleFileUpload}
              />
            )}

            {activeTab === 'sections' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Layout className="h-5 w-5 text-purple-600" />
                    Section Management
                  </h3>
                  
                  {/* Section Visibility Controls */}
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg mb-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Available Sections</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Choose which sections to display on your profile. Checked sections will appear in the order specified below.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {PROFILE_SECTIONS.map((section) => {
                        const IconComponent = section.icon;
                        const isVisible = formData.sectionVisibility[section.key];
                        
                        return (
                          <label 
                            key={section.key}
                            className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              isVisible 
                                ? 'border-purple-300 bg-purple-50' 
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isVisible}
                              onChange={(e) => handleSectionVisibilityChange(section.key, e.target.checked)}
                              className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                            <IconComponent className={`h-5 w-5 mt-0.5 ${isVisible ? 'text-purple-600' : 'text-gray-400'}`} />
                            <div className="flex-1">
                              <div className={`font-medium ${isVisible ? 'text-gray-900' : 'text-gray-500'}`}>
                                {section.name}
                              </div>
                              <div className="text-xs text-gray-500">{section.description}</div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Section Order Management */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-semibold text-gray-900 flex items-center gap-2">
                        <Move className="h-4 w-4 text-purple-600" />
                        Section Order
                      </h4>
                      <button
                        onClick={resetSectionOrder}
                        className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                      >
                        <RefreshCw className="h-3 w-3" />
                        Reset to Default
                      </button>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4">
                      Drag and drop to reorder sections. Only visible sections will appear on your profile.
                    </p>
                    
                    {/* Visible Sections - Draggable List */}
                    <div className="space-y-2 mb-4">
                      <h5 className="text-sm font-medium text-gray-700">Active Sections ({getVisibleSections().length})</h5>
                      <div className="space-y-1">
                        {getVisibleSections().map((sectionKey, index) => {
                          const section = PROFILE_SECTIONS.find(s => s.key === sectionKey);
                          if (!section) return null;
                          
                          const IconComponent = section.icon;
                          
                          return (
                            <div
                              key={sectionKey}
                              draggable
                              onDragStart={(e) => handleDragStart(e, index)}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDrop(e, index)}
                              onDragEnd={handleDragEnd}
                              className={`flex items-center gap-3 p-3 bg-white border rounded-lg cursor-move hover:bg-gray-50 transition-colors ${
                                draggedIndex === index ? 'opacity-50 scale-95' : ''
                              }`}
                            >
                              <div className="flex items-center gap-2 text-gray-400">
                                <span className="text-sm font-mono">{index + 1}</span>
                                <Move className="h-4 w-4" />
                              </div>
                              
                              <IconComponent className="h-5 w-5 text-purple-600" />
                              
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{section.name}</div>
                                <div className="text-xs text-gray-500">{section.description}</div>
                              </div>
                              
                              <button
                                onClick={() => handleSectionVisibilityChange(sectionKey, false)}
                                className="text-gray-400 hover:text-red-500 transition-colors"
                                title="Remove from profile"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      
                      {getVisibleSections().length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <Layout className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                          <p>No sections selected</p>
                          <p className="text-sm">Check sections above to add them to your profile</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Hidden Sections */}
                    {getHiddenSections().length > 0 && (
                      <div className="border-t pt-4">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Hidden Sections ({getHiddenSections().length})</h5>
                        <div className="flex flex-wrap gap-2">
                          {getHiddenSections().map((sectionKey) => {
                            const section = PROFILE_SECTIONS.find(s => s.key === sectionKey);
                            if (!section) return null;
                            
                            const IconComponent = section.icon;
                            
                            return (
                              <button
                                key={sectionKey}
                                onClick={() => handleSectionVisibilityChange(sectionKey, true)}
                                className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-600 rounded-full hover:bg-purple-100 hover:text-purple-700 transition-colors text-sm"
                              >
                                <IconComponent className="h-3 w-3" />
                                {section.name}
                                <Plus className="h-3 w-3" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Section Display Settings */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Section Display Settings</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={formData.showSectionIcons}
                          onChange={(e) => handleInputChange('showSectionIcons', e.target.checked)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Show Section Icons</div>
                          <div className="text-sm text-gray-500">Display icons next to section titles</div>
                        </div>
                      </label>
                      
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={formData.showSectionNumbers}
                          onChange={(e) => handleInputChange('showSectionNumbers', e.target.checked)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Show Section Numbers</div>
                          <div className="text-sm text-gray-500">Number sections sequentially</div>
                        </div>
                      </label>
                      
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={formData.enableSectionCollapse}
                          onChange={(e) => handleInputChange('enableSectionCollapse', e.target.checked)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Collapsible Sections</div>
                          <div className="text-sm text-gray-500">Allow visitors to expand/collapse sections</div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'design' && (
              <DesignBrandingSection
                formData={formData}
                onInputChange={handleInputChange}
                templateType="institution"
              />
            )}

            {activeTab === 'view' && (
              <ViewSettings
                formData={formData}
                onInputChange={handleInputChange}
                templateType="institution"
              />
            )}
            
            

            {activeTab === 'interactions' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">User Interactions</h3>
                  
                  <div className="space-y-4">
                    {[
                      { key: 'allowLikes', label: 'Allow Likes', desc: 'Users can like your profile and sections', icon: Heart },
                      { key: 'allowComments', label: 'Allow Comments', desc: 'Users can comment on profile sections', icon: MessageCircle },
                      { key: 'allowSharing', label: 'Allow Sharing', desc: 'Users can share your profile', icon: Share2 },
                      { key: 'showViewCounts', label: 'Show View Counts', desc: 'Display profile view statistics', icon: Eye },
                      { key: 'showProfileViews', label: 'Show Profile Views', desc: 'Display total profile views', icon: BarChart3 },
                      { key: 'enableContactTracking', label: 'Enable Contact Tracking', desc: 'Track when users use contact methods', icon: Mail },
                      { key: 'allowAnonymousViewing', label: 'Allow Anonymous Viewing', desc: 'Non-logged users can view profile', icon: Users }
                    ].map(({ key, label, desc, icon: Icon }) => (
                      <label key={key} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData[key]}
                          onChange={(e) => handleInputChange(key, e.target.checked)}
                          className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Icon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-900">{label}</div>
                          <div className="text-sm text-gray-500">{desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'contact' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact & Social Settings</h3>
                  
                  {/* Contact Visibility */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">Contact Information Visibility</h4>
                    {[
                      { key: 'email', label: 'Email Address', desc: 'Show email contact information' },
                      { key: 'phone', label: 'Phone Number', desc: 'Show phone contact information' },
                      { key: 'website', label: 'Website', desc: 'Show website link' },
                      { key: 'location', label: 'Location', desc: 'Show location/address' },
                      { key: 'social', label: 'Social Links', desc: 'Show social media links' }
                    ].map(({ key, label, desc }) => (
                      <label key={key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <input
                          type="checkbox"
                          checked={formData.contactVisibility[key]}
                          onChange={(e) => handleNestedInputChange('contactVisibility', key, e.target.checked)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">{label}</div>
                          <div className="text-sm text-gray-500">{desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Social Links */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">Social Media Links</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(formData.socialLinks).map(([platform, url]) => (
                        <div key={platform}>
                          <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                            {platform}
                          </label>
                          <input
                            type="url"
                            value={url}
                            onChange={(e) => handleNestedInputChange('socialLinks', platform, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            placeholder={`https://${platform}.com/yourprofile`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Contact Methods */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">Contact Preferences</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred Contact Method
                      </label>
                      <select
                        value={formData.contactMethods.preferredContact}
                        onChange={(e) => handleNestedInputChange('contactMethods', 'preferredContact', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                        <option value="form">Contact Form</option>
                      </select>
                    </div>

                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={formData.contactMethods.showContactForm}
                        onChange={(e) => handleNestedInputChange('contactMethods', 'showContactForm', e.target.checked)}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <div>
                        <div className="font-medium text-gray-900">Show Contact Form</div>
                        <div className="text-sm text-gray-500">Display a contact form on the profile</div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Shield className="h-5 w-5 text-purple-600" />
                    Privacy & Access Control
                  </h3>

                  {/* Publish Status - NEW SECTION */}
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Globe className="h-5 w-5 text-blue-600" />
                      Publishing Status
                    </h3>
                    
                    <div className="space-y-4">
                      <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                        <input
                          type="checkbox"
                          checked={formData.published}
                          onChange={(e) => setFormData(prev => ({ ...prev, published: e.target.checked }))}
                          className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 font-semibold text-blue-900">
                            <Globe className="h-5 w-5" />
                            Published
                          </div>
                          <div className="text-sm text-blue-700 mt-1">
                            Make this profile visible and accessible to users based on access level settings below. 
                            Unpublished profiles are only visible to you in the editor.
                          </div>
                        </div>
                      </label>
                      
                      {!formData.published && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <div className="flex items-start gap-2">
                            <Lock className="h-5 w-5 text-amber-600 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-amber-800">Profile is currently unpublished</p>
                              <p className="text-sm text-amber-700 mt-1">
                                Only you can see this profile. Enable publishing and set access level to make it visible to others.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Access Level Settings */}
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg mb-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Profile Visibility</h4>
                    
                    <div className="space-y-3">
                      {[
                        { 
                          value: 'public', 
                          label: 'Public', 
                          desc: 'Anyone can view your profile',
                          icon: Globe 
                        },
                        { 
                          value: 'unlisted', 
                          label: 'Unlisted', 
                          desc: 'Only people with the link can view',
                          icon: Link 
                        },
                        { 
                          value: 'private', 
                          label: 'Private', 
                          desc: 'Only you can view your profile',
                          icon: Lock 
                        },
                        { 
                          value: 'restricted', 
                          label: 'Restricted', 
                          desc: 'Only authorized users can view',
                          icon: Users 
                        }
                      ].map(({ value, label, desc, icon: Icon }) => (
                        <label key={value} className="flex items-start gap-3 p-3 bg-white rounded-lg border hover:border-purple-300 cursor-pointer">
                          <input
                            type="radio"
                            name="accessLevel"
                            value={value}
                            checked={formData.accessLevel === value}
                            onChange={(e) => handleInputChange('accessLevel', e.target.value)}
                            className="mt-1 border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <Icon className="h-5 w-5 text-purple-600 mt-0.5" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-sm text-gray-500">{desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Additional Privacy Options */}
                  <div className="space-y-4 mb-6">
                    <h4 className="text-md font-semibold text-gray-900">Additional Privacy Settings</h4>
                    
                    <div className="space-y-3">
                      {[
                        { 
                          key: 'requireLogin', 
                          label: 'Require Login to View', 
                          desc: 'Users must be logged in to view profile',
                          icon: UserCheck
                        },
                        { 
                          key: 'allowIndexing', 
                          label: 'Allow Search Engine Indexing', 
                          desc: 'Let search engines index your profile',
                          icon: Globe
                        },
                        { 
                          key: 'allowPrint', 
                          label: 'Allow Printing', 
                          desc: 'Users can print your profile',
                          icon: Printer
                        },
                        { 
                          key: 'enableRightClickProtection', 
                          label: 'Enable Right-Click Protection', 
                          desc: 'Disable right-click context menu',
                          icon: Shield
                        }
                      ].map(({ key, label, desc, icon: Icon }) => (
                        <label key={key} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={formData[key]}
                            onChange={(e) => handleInputChange(key, e.target.checked)}
                            className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <Icon className="h-5 w-5 text-purple-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-sm text-gray-500">{desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Authorized Users (only show if restricted access) */}
                  {formData.accessLevel === 'restricted' && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <Users className="h-4 w-4 text-purple-600" />
                        Authorized Users
                      </h4>
                      
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <input
                            type="email"
                            value={newAuthorizedUser}
                            onChange={(e) => setNewAuthorizedUser(e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            placeholder="email@example.com"
                          />
                          <button
                            onClick={() => {
                              if (newAuthorizedUser && !formData.authorizedUsers.includes(newAuthorizedUser)) {
                                handleInputChange('authorizedUsers', [...formData.authorizedUsers, newAuthorizedUser]);
                                setNewAuthorizedUser('');
                              }
                            }}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                        
                        {formData.authorizedUsers.length > 0 && (
                          <div className="space-y-2">
                            {formData.authorizedUsers.map((email, index) => (
                              <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                <span className="text-sm text-gray-700">{email}</span>
                                <button
                                  onClick={() => {
                                    const updatedUsers = formData.authorizedUsers.filter((_, i) => i !== index);
                                    handleInputChange('authorizedUsers', updatedUsers);
                                  }}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Data Export Section */}
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Download className="h-5 w-5 text-amber-600" />
                      Data Export
                    </h4>
                    
                    <p className="text-sm text-gray-600 mb-4">
                      Export your profile data for backup or migration purposes. This includes all profile information, sections, and metadata.
                    </p>
                    
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={async () => {
                          try {
                            setUploading(true);
                            await handleExportProfile('csv');
                          } catch (error) {
                            console.error('Export error:', error);
                            alert('Failed to export profile data');
                          } finally {
                            setUploading(false);
                          }
                        }}
                        disabled={uploading}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {uploading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <File className="h-4 w-4" />
                        )}
                        Export as CSV
                      </button>
                      
                      <button
                        onClick={async () => {
                          try {
                            setUploading(true);
                            await handleExportProfile('excel');
                          } catch (error) {
                            console.error('Export error:', error);
                            alert('Failed to export profile data');
                          } finally {
                            setUploading(false);
                          }
                        }}
                        disabled={uploading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        {uploading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <BarChart3 className="h-4 w-4" />
                        )}
                        Export as Excel
                      </button>
                      
                      <button
                        onClick={async () => {
                          try {
                            setUploading(true);
                            await handleExportProfile('json');
                          } catch (error) {
                            console.error('Export error:', error);
                            alert('Failed to export profile data');
                          } finally {
                            setUploading(false);
                          }
                        }}
                        disabled={uploading}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                      >
                        {uploading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Code className="h-4 w-4" />
                        )}
                        Export as JSON
                      </button>
                    </div>
                    
                    <div className="mt-3 text-xs text-gray-500 bg-white p-2 rounded border">
                      <strong>Export includes:</strong> Basic info, all profile sections, media URLs, styling settings, SEO data, and analytics data. Sensitive information like authorized user emails are excluded for privacy.
                    </div>
                  </div>

                  {/* Analytics Settings */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-purple-600" />
                      Analytics & Tracking
                    </h4>
                    
                    <div className="space-y-3">
                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={formData.enableAnalytics}
                          onChange={(e) => handleInputChange('enableAnalytics', e.target.checked)}
                          className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Enable Analytics</div>
                          <div className="text-sm text-gray-500">Track profile views, interactions, and user engagement</div>
                        </div>
                      </label>
                      
                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={formData.showViewCounts}
                          onChange={(e) => handleInputChange('showViewCounts', e.target.checked)}
                          className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Show View Counts</div>
                          <div className="text-sm text-gray-500">Display view counts publicly on your profile</div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'seo' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Globe className="h-5 w-5 text-purple-600" />
                    SEO & Social Media
                  </h3>
                  
                  {/* URL and Domain Settings */}
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg mb-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">URL Configuration</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Custom Domain
                        </label>
                        <input
                          type="text"
                          value={formData.customDomain}
                          onChange={(e) => handleInputChange('customDomain', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="profile.yourdomain.com"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Point your domain to our servers via DNS
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Custom Path
                        </label>
                        <input
                          type="text"
                          value={formData.customPath}
                          onChange={(e) => handleInputChange('customPath', e.target.value.replace(/[^a-zA-Z0-9-]/g, ''))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="my-profile"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Letters, numbers, and hyphens only
                        </p>
                      </div>
                    </div>

                    {/* URL Preview */}
                    {(formData.customDomain || formData.customPath) && (
                      <div className="mt-4 p-3 bg-white border rounded-lg">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-gray-500">Your profile will be available at:</span>
                          <div className="font-medium text-blue-600">
                            {formData.customDomain ? 
                              `https://${formData.customDomain}` : 
                              `https://parrotpages.com/${formData.customPath || 'your-profile'}`
                            }
                          </div>
                          <ExternalLink className="h-3 w-3 text-gray-400" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* SEO Content */}
                  <div className="space-y-4 mb-6">
                    <h4 className="text-md font-semibold text-gray-900">SEO Content</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Title
                      </label>
                      <input
                        type="text"
                        value={formData.seoTitle}
                        onChange={(e) => handleInputChange('seoTitle', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Title for search engines (50-60 characters)"
                        maxLength="60"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.seoTitle.length}/60 characters
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Description
                      </label>
                      <textarea
                        value={formData.seoDescription}
                        onChange={(e) => handleInputChange('seoDescription', e.target.value)}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Description for search engines and social media (150-160 characters)"
                        maxLength="160"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.seoDescription.length}/160 characters
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Keywords
                      </label>
                      <input
                        type="text"
                        value={formData.seoKeywords}
                        onChange={(e) => handleInputChange('seoKeywords', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="keyword1, keyword2, keyword3"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Separate keywords with commas
                      </p>
                    </div>
                  </div>

                  {/* SEO Images */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-4">SEO Images</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Favicon */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Favicon
                        </label>
                        <FileUpload
                          label="Upload Favicon"
                          currentFile={formData.favicon}
                          onFileSelect={async (file, error) => {
                            if (error) {
                              setErrors(prev => ({ ...prev, favicon: error }));
                              return;
                            }
                            
                            setUploading(true);
                            try {
                              const result = await profileService.uploadProfileMedia(
                                file, 
                                profile?.id || 'temp', 
                                'seo',
                                'favicon'
                              );
                              handleInputChange('favicon', result.url);
                            } catch (error) {
                              setErrors(prev => ({ ...prev, favicon: error.message }));
                            } finally {
                              setUploading(false);
                            }
                          }}
                          onRemove={() => handleInputChange('favicon', '')}
                          accept="image/x-icon,image/png,image/svg+xml"
                          maxSize={1}
                          uploading={uploading}
                          error={errors.favicon}
                          showPreview={true}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          16x16 or 32x32 pixels, .ico, .png, or .svg format
                        </p>
                        {formData.favicon && (
                          <div className="mt-2 flex items-center gap-2">
                            <img 
                              src={formData.favicon} 
                              alt="Favicon preview"
                              className="w-4 h-4"
                            />
                            <span className="text-xs text-gray-600">Favicon preview</span>
                          </div>
                        )}
                      </div>

                      {/* Open Graph Image */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Open Graph Image
                        </label>
                        <FileUpload
                          label="Upload OG Image"
                          currentFile={formData.ogImage}
                          onFileSelect={async (file, error) => {
                            if (error) {
                              setErrors(prev => ({ ...prev, ogImage: error }));
                              return;
                            }
                            
                            setUploading(true);
                            try {
                              const result = await profileService.uploadProfileMedia(
                                file, 
                                profile?.id || 'temp', 
                                'seo',
                                'og-image'
                              );
                              handleInputChange('ogImage', result.url);
                            } catch (error) {
                              setErrors(prev => ({ ...prev, ogImage: error.message }));
                            } finally {
                              setUploading(false);
                            }
                          }}
                          onRemove={() => handleInputChange('ogImage', '')}
                          accept="image/*"
                          maxSize={5}
                          uploading={uploading}
                          error={errors.ogImage}
                          showPreview={true}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          1200x630 pixels recommended for social media sharing
                        </p>
                        {formData.ogImage && (
                          <div className="mt-2">
                            <div className="w-full max-w-[200px] h-[105px] rounded border bg-gray-100 overflow-hidden">
                              <img 
                                src={formData.ogImage} 
                                alt="OG image preview"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Advanced SEO Settings */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Advanced SEO</h4>
                    
                    <div className="space-y-3">
                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={formData.enableStructuredData}
                          onChange={(e) => handleInputChange('enableStructuredData', e.target.checked)}
                          className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Enable Structured Data</div>
                          <div className="text-sm text-gray-500">Add JSON-LD structured data for better search engine understanding</div>
                        </div>
                      </label>

                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={formData.allowIndexing}
                          onChange={(e) => handleInputChange('allowIndexing', e.target.checked)}
                          className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Allow Search Engine Indexing</div>
                          <div className="text-sm text-gray-500">Let search engines index and display your profile</div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'integrations' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Third-party Integrations</h3>
                  
                  <div className="space-y-6">
                    {/* Analytics Integrations */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Analytics</h4>
                      <div className="space-y-4">
                        <div className="p-4 border border-gray-200 rounded-lg">
                          <label className="flex items-center gap-3 mb-3">
                            <input
                              type="checkbox"
                              checked={formData.integrations.googleAnalytics.enabled}
                              onChange={(e) => handleNestedInputChange('integrations', 'googleAnalytics', { 
                                ...formData.integrations.googleAnalytics, 
                                enabled: e.target.checked 
                              })}
                              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">Google Analytics</div>
                              <div className="text-sm text-gray-500">Track profile visits and user behavior</div>
                            </div>
                          </label>
                          {formData.integrations.googleAnalytics.enabled && (
                            <input
                              type="text"
                              value={formData.integrations.googleAnalytics.trackingId}
                              onChange={(e) => handleNestedInputChange('integrations', 'googleAnalytics', { 
                                ...formData.integrations.googleAnalytics, 
                                trackingId: e.target.value 
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                              placeholder="GA-XXXXXXXXX-X"
                            />
                          )}
                        </div>

                        <div className="p-4 border border-gray-200 rounded-lg">
                          <label className="flex items-center gap-3 mb-3">
                            <input
                              type="checkbox"
                              checked={formData.integrations.facebookPixel.enabled}
                              onChange={(e) => handleNestedInputChange('integrations', 'facebookPixel', { 
                                ...formData.integrations.facebookPixel, 
                                enabled: e.target.checked 
                              })}
                              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">Facebook Pixel</div>
                              <div className="text-sm text-gray-500">Track conversions and optimize ads</div>
                            </div>
                          </label>
                          {formData.integrations.facebookPixel.enabled && (
                            <input
                              type="text"
                              value={formData.integrations.facebookPixel.pixelId}
                              onChange={(e) => handleNestedInputChange('integrations', 'facebookPixel', { 
                                ...formData.integrations.facebookPixel, 
                                pixelId: e.target.value 
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                              placeholder="123456789012345"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Booking Integration */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Scheduling</h4>
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <label className="flex items-center gap-3 mb-3">
                          <input
                            type="checkbox"
                            checked={formData.integrations.calendly.enabled}
                            onChange={(e) => handleNestedInputChange('integrations', 'calendly', { 
                              ...formData.integrations.calendly, 
                              enabled: e.target.checked 
                            })}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">Calendly Integration</div>
                            <div className="text-sm text-gray-500">Allow visitors to book meetings directly</div>
                          </div>
                        </label>
                        {formData.integrations.calendly.enabled && (
                          <input
                            type="text"
                            value={formData.integrations.calendly.username}
                            onChange={(e) => handleNestedInputChange('integrations', 'calendly', { 
                              ...formData.integrations.calendly, 
                              username: e.target.value 
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            placeholder="your-calendly-username"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'advanced' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Advanced Settings</h3>
                  
                  <div className="space-y-6">
                    {/* Custom CSS */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom CSS
                      </label>
                      <textarea
                        value={formData.customCSS}
                        onChange={(e) => handleInputChange('customCSS', e.target.value)}
                        rows="8"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                        placeholder="/* Add your custom CSS here */
.profile-header {
  background: linear-gradient(45deg, #667eea 0%, #764ba2 100%);
}

.profile-section {
  margin-bottom: 2rem;
}"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Custom CSS will be applied to your profile. Use with caution.
                      </p>
                    </div>

                    {/* Advanced Options */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Security & Protection</h4>
                      {[
                        { key: 'enableRightClickProtection', label: 'Disable Right-Click', desc: 'Prevent right-click context menu' },
                        { key: 'enableDevTools', label: 'Disable Developer Tools', desc: 'Attempt to prevent developer tools access' },
                        { key: 'enablePrintProtection', label: 'Disable Printing', desc: 'Prevent users from printing the profile' }
                      ].map(({ key, label, desc }) => (
                        <label key={key} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={formData[key]}
                            onChange={(e) => handleInputChange(key, e.target.checked)}
                            className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <div>
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-sm text-gray-500">{desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>

                    {/* Analytics Options */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Analytics & Tracking</h4>
                      {[
                        { key: 'enableAnalytics', label: 'Enable Analytics', desc: 'Track profile views and interactions' },
                        { key: 'enableHeatmaps', label: 'Enable Heatmaps', desc: 'Track user behavior with heatmaps' }
                      ].map(({ key, label, desc }) => (
                        <label key={key} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={formData[key]}
                            onChange={(e) => handleInputChange(key, e.target.checked)}
                            className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <div>
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-sm text-gray-500">{desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'preview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Preview</h3>
                  
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <div className="bg-white rounded-lg shadow-sm p-6" style={{
                      backgroundColor: formData.backgroundColor,
                      fontFamily: formData.fontFamily
                    }}>
                      {/* Preview Header */}
                      <div className="text-center mb-6" style={{ backgroundColor: formData.headerColor }}>
                        {formData.profileImageUrl && (
                          <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-4 border-white shadow-lg">
                            <img src={formData.profileImageUrl} alt="Profile" className="w-full h-full object-cover" />
                          </div>
                        )}
                        <h1 className="text-2xl font-bold mb-2" style={{ color: formData.headerTextColor }}>
                          {formData.botName || 'Profile Name'}
                        </h1>
                        <p className="text-gray-600">{formData.description || 'Profile description goes here'}</p>
                      </div>

                      {/* Preview Sections */}
                      <div className="space-y-4">
                        {formData.sectionOrder
                          .filter(section => formData.sectionVisibility[section])
                          .slice(0, 3)
                          .map(section => (
                            <div key={section} className={`p-4 rounded-lg border`} style={{
                              borderColor: formData.primaryColor + '20',
                              backgroundColor: formData.primaryColor + '05'
                            }}>
                              <h3 className="font-semibold mb-2 capitalize flex items-center gap-2" style={{ color: formData.primaryColor }}>
                                {formData.showSectionIcons && '📄'}
                                {section.replace(/([A-Z])/g, ' $1')}
                              </h3>
                              <p className="text-sm text-gray-600">
                                This is a preview of the {section} section content.
                              </p>
                            </div>
                          ))}
                      </div>

                      {/* Preview Footer */}
                      <div className="mt-6 pt-4 border-t text-center">
                        <p className="text-xs text-gray-500">
                          This is a preview of your profile with current settings
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="text-center">
                    <button
                      onClick={() => window.open(`/books/${profile.id}`, '_blank')}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open Full Preview
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};